import { useAuth } from '@/store/hooks'
import { MdError, MdInfo, MdPayment, MdWarning } from 'react-icons/md'
import { Link } from 'react-router-dom'

export default function SubscriptionAlert() {
    const { user } = useAuth()

    if (!user?.company?.organization?.subscription) {
        return null
    }

    const subscription = user.company.organization.subscription

    const effectivePrice =
        (subscription as any).customPrice ??
        subscription.plan?.customPrice ??
        subscription.plan?.price ??
        0

    if (Number(effectivePrice) <= 0) {
        return null
    }

    const now = new Date()
    const currentDay = now.getDate()
    const currentMonth = now.getMonth()
    const currentYear = now.getFullYear()

    const dueDay = subscription.dueDay
    const isTodayDueDay = currentDay === dueDay
    const isDueDatePassed = currentDay > dueDay

    // Período grátis do link de cadastro: o `dueDay` nasce no dia em que a
    // cortesia acaba, então sem esta checagem a conta criada depois desse dia
    // do mês já entraria vendo "Pagamento pendente". Mesmas regras do backend
    // (`libs/common/subscription/trial.ts`): aviso só nos últimos 3 dias.
    const trialEndsAt = subscription.trialEndsAt
        ? new Date(subscription.trialEndsAt)
        : null
    const isInTrial =
        !!trialEndsAt &&
        !Number.isNaN(trialEndsAt.getTime()) &&
        trialEndsAt.getTime() > now.getTime()
    const trialDaysRemaining = isInTrial
        ? Math.ceil(
              (trialEndsAt!.getTime() - now.getTime()) / (24 * 60 * 60 * 1000)
          )
        : 0

    let shouldShowAlert = false
    let alertType: 'warning' | 'error' | 'info' = 'warning'
    let alertMessage = ''
    let alertIcon = <MdWarning className="text-xl" />

    if (subscription.status === 'SUSPENDED') {
        // Suspensão manual vence a cortesia, como no SubscriptionGuard
        shouldShowAlert = true
        alertType = 'error'
        alertMessage =
            'Sua assinatura está suspensa. Regularize o pagamento para continuar utilizando o sistema.'
        alertIcon = <MdError className="text-xl" />
    } else if (isInTrial) {
        if (trialDaysRemaining <= 3) {
            shouldShowAlert = true
            alertType = 'info'
            alertMessage = `Seu período grátis termina em ${trialDaysRemaining} ${
                trialDaysRemaining === 1 ? 'dia' : 'dias'
            } (${trialEndsAt!.toLocaleDateString('pt-BR')}). Realize o pagamento para continuar utilizando o sistema sem interrupções.`
            alertIcon = <MdInfo className="text-xl" />
        }
    } else if (isDueDatePassed) {
        const lastPaymentDate = subscription.lastPaymentDate
            ? new Date(subscription.lastPaymentDate)
            : null

        const hasPaymentForCurrentPeriod =
            lastPaymentDate &&
            lastPaymentDate.getMonth() === currentMonth &&
            lastPaymentDate.getFullYear() === currentYear

        if (!hasPaymentForCurrentPeriod) {
            shouldShowAlert = true
            alertType = 'warning'
            alertMessage = `Pagamento pendente! O vencimento da sua assinatura é todo dia ${dueDay}. Realize o pagamento para evitar interrupções.`
            alertIcon = <MdPayment className="text-xl" />
        }
    } else if (isTodayDueDay) {
        const lastPaymentDate = subscription.lastPaymentDate
            ? new Date(subscription.lastPaymentDate)
            : null

        const hasPaymentForCurrentPeriod =
            lastPaymentDate &&
            lastPaymentDate.getMonth() === currentMonth &&
            lastPaymentDate.getFullYear() === currentYear

        if (!hasPaymentForCurrentPeriod) {
            shouldShowAlert = true
            alertType = 'info'
            alertMessage = `Hoje é o dia do vencimento da sua assinatura (dia ${dueDay}). Você já pode realizar o pagamento.`
            alertIcon = <MdInfo className="text-xl" />
        }
    }

    if (!shouldShowAlert) {
        return null
    }

    return (
        <div
            className={`${
                alertType === 'error'
                    ? 'bg-error/10 border-error'
                    : alertType === 'warning'
                      ? 'bg-warning/10 border-warning'
                      : 'bg-info/10 border-info'
            } border-b-2 px-4 py-3`}
        >
            <div className="container mx-auto">
                <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 flex-1">
                        <div
                            className={`${
                                alertType === 'error'
                                    ? 'text-error'
                                    : alertType === 'warning'
                                      ? 'text-warning'
                                      : 'text-info'
                            }`}
                        >
                            {alertIcon}
                        </div>
                        <p
                            className={`text-sm font-medium ${
                                alertType === 'error'
                                    ? 'text-error'
                                    : alertType === 'warning'
                                      ? 'text-warning'
                                      : 'text-info'
                            }`}
                        >
                            {alertMessage}
                        </p>
                    </div>
                    <Link
                        to="/account?tab=financial"
                        className={`btn btn-sm ${
                            alertType === 'error'
                                ? 'btn-error'
                                : alertType === 'warning'
                                  ? 'btn-warning'
                                  : 'btn-info'
                        } text-white`}
                    >
                        {alertType === 'info'
                            ? 'Realizar Pagamento'
                            : 'Regularizar Pagamento'}
                    </Link>
                </div>
            </div>
        </div>
    )
}
